/**
 * DataBest — Quantitative Summary section for the Individual BIRP report.
 * Renders KPIs + Radar + Trend + Life Area breakdown + Outcome breakdown,
 * scoped to the report's individual + period.
 */
import React from "react";
import { Activity, Star, ThumbsUp } from "lucide-react";
import KpiCard from "@/features/dashboard/components/KpiCard";
import DashboardRadar from "@/features/dashboard/components/DashboardRadar";
import DashboardTrend from "@/features/dashboard/components/DashboardTrend";
import BreakdownTable from "@/features/dashboard/components/BreakdownTable";
import { formatDateUS } from "@/lib/dates";

const BirpQuantitativeSection = ({ quantitative, periodStart, periodEnd }) => {
    if (!quantitative) return null;

    const kpis = quantitative.kpis || {};
    const radar = quantitative.radar || { axes: [], sample_size: 0 };
    const trendSeries = quantitative.trend || null;
    const lifeArea = quantitative.breakdown_by_life_area || [];
    const outcomes = quantitative.breakdown_by_outcome || [];

    const startLabel = formatDateUS(periodStart);
    const endLabel = periodEnd ? formatDateUS(periodEnd) : startLabel;
    const rangeText =
        periodEnd && periodEnd !== periodStart
            ? `${startLabel} → ${endLabel}`
            : startLabel;

    return (
        <div
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6"
            data-testid="birp-quantitative-section"
        >
            <div className="mb-4 border-b border-slate-100 pb-3">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-blue-700">
                    Quantitative Summary
                </p>
                <p className="mt-1 text-sm text-slate-500">
                    📅 {rangeText}
                    {kpis.total_measurements != null
                        ? ` · ${kpis.total_measurements} measurements`
                        : ""}
                </p>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <KpiCard
                    icon={Activity}
                    tone="blue"
                    label="Measurements"
                    value={kpis.total_measurements ?? "—"}
                    hint="In this period"
                    testId="birp-kpi-total"
                    loading={false}
                />
                <KpiCard
                    icon={Star}
                    tone="green"
                    label="Avg Plan Score"
                    value={
                        kpis.avg_plan_score != null ? kpis.avg_plan_score.toFixed(2) : "—"
                    }
                    hint={
                        kpis.avg_plan_letter_grade
                            ? `${kpis.avg_plan_letter_grade}${
                                  kpis.avg_plan_gpa != null
                                      ? ` · GPA ${kpis.avg_plan_gpa.toFixed(1)}`
                                      : ""
                              }${kpis.avg_plan_qualitative ? " · " + kpis.avg_plan_qualitative : ""}`
                            : "(%D + %Q + (100−%Dep)) / 3"
                    }
                    testId="birp-kpi-avg"
                    loading={false}
                />
                <KpiCard
                    icon={ThumbsUp}
                    tone="amber"
                    label="Yes to supports"
                    value={
                        kpis.yes_to_supports_pct != null
                            ? `${kpis.yes_to_supports_pct}%`
                            : "—"
                    }
                    hint="Response yes rate"
                    testId="birp-kpi-yes"
                    loading={false}
                />
            </div>

            {/* Radar + Trend */}
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <DashboardRadar radar={radar} loading={false} />
                {trendSeries && trendSeries.length > 0 ? (
                    <DashboardTrend
                        trend={{ granularity: "day", series: trendSeries }}
                        loading={false}
                    />
                ) : (
                    <div className="hidden lg:block" />
                )}
            </div>

            {/* Breakdown by Life Area */}
            <div className="mt-5">
                <BreakdownTable
                    breakdown={{ group_by: "life_area", rows: lifeArea }}
                    groupBy="life_area"
                    onGroupChange={() => {}}
                    loading={false}
                    hideGroupSelector
                    title="Breakdown by Life Area"
                />
            </div>

            {/* Breakdown by Outcome */}
            <div className="mt-4">
                <BreakdownTable
                    breakdown={{ group_by: "outcome", rows: outcomes }}
                    groupBy="outcome"
                    onGroupChange={() => {}}
                    loading={false}
                    hideGroupSelector
                    title="Breakdown by Outcome"
                />
            </div>
        </div>
    );
};

export default BirpQuantitativeSection;
